import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { NavLink, Outlet } from 'react-router-dom';
import Text from '../../../../components/Text';
import { styles } from '../../../../styles/themes/style';
import { AppRoutes } from '../../../../interfaces/store/appStore';
import { useAuthStore } from '../../../../stores/auth/useAuthStore';

/**
 * Settings → Campaign settings: what every campaign in the workspace shares, a tab each.
 *
 * The tabs are child routes, so each one keeps its own URL — links straight to a tab
 * (the account-limit banner's "Adjust limits", the old /settings/… addresses) still work.
 */

const TABS: { label: string; to: AppRoutes; premium: boolean }[] = [
  { label: 'Account activity', to: AppRoutes.SETTINGS_ACCOUNT_ACTIVITY, premium: false },
  { label: 'Sending accounts', to: AppRoutes.SETTINGS_SENDING_ACCOUNTS, premium: true },
  { label: 'Suppression lists', to: AppRoutes.SETTINGS_SUPPRESSION_LIST, premium: true },
];

const CampaignSettings: FunctionComponent = () => {
  const { store: { disablePremiumFeatures } } = useAuthStore();
  const tabs = TABS.filter(tab => !tab.premium || !disablePremiumFeatures);

  return (
    <Container>
      <Header>
        <Text $heading4>Campaign settings</Text>
        <Text $lighter>Limits, suppression lists and sending accounts shared by every campaign in this workspace.</Text>
      </Header>

      <Tabs role="tablist">
        {tabs.map(tab => (
          <Tab key={tab.to} to={tab.to} role="tab">{tab.label}</Tab>
        ))}
      </Tabs>

      <Outlet />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: calc(100vh - 130px);
  overflow-y: auto;

  @media (max-width: 1240px) {
    max-height: none;
    overflow-y: visible;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// No overflow: the active tab's underline sits 1px over the border, and a scroll
// container would turn that 1px into a scrollbar.
const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 28px;
  border-bottom: 1px solid ${styles.colors.black200};
`;

const Tab = styled(NavLink)`
  padding: 10px 2px;
  margin-bottom: -1px;
  border-bottom: 2px solid transparent;
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 700;
  color: ${styles.colors.black400};
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.14s, border-color 0.14s;

  &:hover { color: ${styles.colors.primary600}; }

  &.active {
    color: ${styles.colors.primary600};
    border-bottom-color: ${styles.colors.primary600};
  }
`;

export default CampaignSettings;
